import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TurndownService from 'turndown';
import { marked } from 'marked';

const markdown = new TurndownService({ headingStyle: 'atx', bulletListMarker: '-', emDelimiter: '*' });
export default function EssayEditor({ value, onChange, toolbarContainer }: { value: string; onChange: (value: string) => void; toolbarContainer?: HTMLElement | null }) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [linkError, setLinkError] = useState('');
  const selection = useRef({ from: 1, to: 1 });
  const currentValue = useRef(value);
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] } }), Link.configure({ openOnClick: false })],
    content: marked.parse(value, { async: false }) as string,
    editorProps: { attributes: { role: 'textbox', 'aria-label': 'Essay content', 'aria-multiline': 'true', class: 'essay-rich-body article-reading' } },
    onUpdate: ({ editor }) => {
      const text = markdown.turndown(editor.getHTML());
      currentValue.current = text;
      onChange(text);
    },
  });
  useEffect(() => {
    if (editor && value !== currentValue.current) {
      editor.commands.setContent(marked.parse(value, { async: false }) as string, false);
      currentValue.current = value;
    }
  }, [editor, value]);
  if (!editor) return null;
  const toolbar = <div className="essay-formatting" role="group" aria-label="Text formatting">
    <button type="button" aria-label="Bold" aria-pressed={editor.isActive('bold')} onMouseDown={event => event.preventDefault()} onClick={() => editor.chain().focus().toggleBold().run()}><strong>B</strong></button>
    <button type="button" aria-label="Italic" aria-pressed={editor.isActive('italic')} onMouseDown={event => event.preventDefault()} onClick={() => editor.chain().focus().toggleItalic().run()}><em>I</em></button>
    <button type="button" aria-pressed={editor.isActive('heading')} onMouseDown={event => event.preventDefault()} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>Heading</button>
    <button type="button" aria-pressed={editor.isActive('blockquote')} onMouseDown={event => event.preventDefault()} onClick={() => editor.chain().focus().toggleBlockquote().run()}>Quote</button>
    <div className="essay-popover-anchor">
      <button type="button" aria-expanded={listOpen} onMouseDown={event => event.preventDefault()} onClick={() => setListOpen(open => !open)}>List ⌄</button>
      {listOpen && <div className="essay-popover essay-section-options" role="group" aria-label="List style">
        <button type="button" aria-pressed={editor.isActive('bulletList')} onMouseDown={event => event.preventDefault()} onClick={() => { editor.chain().focus().toggleBulletList().run(); setListOpen(false); }}>Bulleted list</button>
        <button type="button" aria-pressed={editor.isActive('orderedList')} onMouseDown={event => event.preventDefault()} onClick={() => { editor.chain().focus().toggleOrderedList().run(); setListOpen(false); }}>Numbered list</button>
      </div>}
    </div>
    <div className="essay-popover-anchor">
      <button type="button" aria-expanded={linkOpen} onMouseDown={event => event.preventDefault()} onClick={() => {
        selection.current = editor.state.selection;
        setUrl(editor.getAttributes('link').href || ''); setLinkError(''); setLinkOpen(open => !open);
      }}>Link</button>
      {linkOpen && <div className="essay-popover" role="dialog" aria-label="Insert link" onKeyDown={event => { if (event.key === 'Escape') { setLinkOpen(false); editor.commands.focus(); } }}>
        <label htmlFor="essay-link-url">Link URL</label>
        <input autoFocus id="essay-link-url" type="url" value={url} onChange={event => setUrl(event.target.value)} placeholder="https://…" />
        {linkError && <p role="alert">{linkError}</p>}
        <div className="essay-link-actions"><button type="button" onClick={() => {
          try { const parsed = new URL(url.trim()); if (!['https:', 'http:'].includes(parsed.protocol)) throw new Error(); }
          catch { setLinkError('Enter a valid http or https URL.'); return; }
          const chain = editor.chain().focus().setTextSelection(selection.current);
          if (selection.current.from === selection.current.to && !editor.isActive('link')) chain.insertContent({ type: 'text', text: url.trim(), marks: [{ type: 'link', attrs: { href: url.trim() } }] }).run();
          else chain.extendMarkRange('link').setLink({ href: url.trim() }).run();
          setLinkOpen(false);
        }}>Apply</button><button type="button" onClick={() => { editor.chain().focus().setTextSelection(selection.current).extendMarkRange('link').unsetLink().run(); setLinkOpen(false); }}>Remove</button><button type="button" onClick={() => { setLinkOpen(false); editor.commands.focus(); }}>Cancel</button></div>
      </div>}
    </div>
  </div>;
  return <>{toolbarContainer ? createPortal(toolbar, toolbarContainer) : toolbar}<EditorContent editor={editor} /></>;
}
